import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

export const useAtencionFilters = () => {
  const { solicitudes, loading } = useSelector(state => state.atencionReducer);
  
  const [filtros, setFiltros] = useState({
    busqueda: '',
    estado: '',
    soloNoLeidos: false
  });

  const [paginacion, setPaginacion] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const tableRef = useRef(null);

  // filtrar solicitudes
  const filteredData = useMemo(() => {
    let resultado = [...solicitudes];

    // filtro por búsqueda global (id, descripción, usuario o proveedor)
    if (filtros.busqueda) {
      const busqueda = filtros.busqueda.toLowerCase();
      resultado = resultado.filter(solicitud =>
        solicitud.proveedorSolicitudID.toString().includes(busqueda) ||
        solicitud.comentario.toLowerCase().includes(busqueda) ||
        solicitud.proveedorUsuarioNombre?.toLowerCase().includes(busqueda) ||
        solicitud.proveedorNombre?.toLowerCase().includes(busqueda)
      );
    }

    // filtro por estado (solo cuando se hace clic en contadores)
    if (filtros.estado) {
      if (filtros.estado === 'abierto') {
        resultado = resultado.filter(solicitud => 
          solicitud.estatus?.toLowerCase() === 'abierto' || solicitud.estatus?.toLowerCase() === 'en proceso'
        );
      } else {
        resultado = resultado.filter(solicitud => solicitud.estatus?.toLowerCase() === filtros.estado.toLowerCase());
      }
    }

    // filtro por no leídos (solo cuando se hace clic en contador)
    if (filtros.soloNoLeidos) {
      resultado = resultado.filter(solicitud =>
        solicitud.ultimoComentarioHistorial && !solicitud.ultimoComentarioHistorial.leido
      );
    }

    return resultado;
  }, [solicitudes, filtros]);

  // contadores
  const contadores = useMemo(() => {
    const total = solicitudes.length;
    const abiertas = solicitudes.filter(s => 
      s.estatus?.toLowerCase() === 'abierto' || s.estatus?.toLowerCase() === 'en proceso'
    ).length;
    const cerradas = solicitudes.filter(s => s.estatus?.toLowerCase() === 'cerrado').length;
    const noLeidas = solicitudes.filter(s => 
      s.ultimoComentarioHistorial && !s.ultimoComentarioHistorial.leido
    ).length;

    return { total, abiertas, cerradas, noLeidas };
  }, [solicitudes]);

  // opciones para filtros de estado (solo para uso interno de tabla)
  const opcionesEstados = useMemo(() => {
    const estados = [...new Set(solicitudes.map(s => s.estatus))].filter(Boolean);
    return estados.map(e => ({ label: e, value: e }));
  }, [solicitudes]);

  // info del filtro activo
  const filterInfo = useMemo(() => {
    if (filtros.soloNoLeidos) {
      return {
        label: "Solicitudes no leídas",
        count: filteredData.length
      };
    }
    if (filtros.estado) {
      return {
        label: `Solicitudes ${filtros.estado.toLowerCase()}`,
        count: filteredData.length
      };
    }
    if (filtros.busqueda) {
      return {
        label: `Búsqueda: "${filtros.busqueda}"`,
        count: filteredData.length
      };
    }
    return { label: "", count: 0 };
  }, [filtros, filteredData.length]);

  const isFiltered = useMemo(() => {
    return filtros.busqueda || filtros.estado || filtros.soloNoLeidos;
  }, [filtros]);

  // funciones
  const actualizarFiltro = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
    setPaginacion(prev => ({ ...prev, current: 1 })); // reset página
  };

  const limpiarFiltros = () => {
    setFiltros({
      busqueda: '',
      estado: '',
      soloNoLeidos: false
    });
    setPaginacion(prev => ({ ...prev, current: 1 }));
    
    // limpiar filtros de tabla también
    if (tableRef.current?.clearAllFilters) {
      tableRef.current.clearAllFilters();
    }
  };

  const handleTableChange = ({ filteredData: newFilteredData, filters, sorter, pagination }) => {
    // actualizar datos filtrados por tabla
    // este método es llamado por la tabla cuando cambian sus filtros internos
  };

  const handleStatusFilterClick = (status) => {
    if (filtros.estado === status) {
      limpiarFiltros();
    } else {
      setFiltros({
        busqueda: '',
        estado: status,
        soloNoLeidos: false
      });
      setPaginacion(prev => ({ ...prev, current: 1 }));
    }
  };

  const handleNoLeidosFilterClick = () => {
    if (filtros.soloNoLeidos) {
      limpiarFiltros();
    } else {
      setFiltros({
        busqueda: '',
        estado: '',
        soloNoLeidos: true
      });
      setPaginacion(prev => ({ ...prev, current: 1 }));
    }
  };

  const handleTotalFilterClick = () => {
    limpiarFiltros();
  };

  const handleResetAllFilters = () => {
    limpiarFiltros();
  };

  // actualizar total de paginación cuando cambian los datos filtrados
  useEffect(() => {
    setPaginacion(prev => ({
      ...prev,
      total: filteredData.length
    }));
  }, [filteredData.length]);

  return {
    // datos
    solicitudes,
    filteredData,
    loading,
    contadores,
    
    // filtros
    filtros,
    opcionesEstados,
    filterInfo,
    isFiltered,
    
    // paginación
    paginacion,
    setPaginacion,
    
    // referencias
    tableRef,
    
    // funciones
    actualizarFiltro,
    limpiarFiltros,
    handleTableChange,
    handleStatusFilterClick,
    handleNoLeidosFilterClick,
    handleTotalFilterClick,
    handleResetAllFilters
  };
};